import CachedIcon from '@mui/icons-material/Cached';
import { Typography as NewTypography } from '@mui/joy';
import {
  Box,
  Divider,
  Stack,
  Typography,
  Skeleton,
  Button,
} from '@mui/material';
import dayjs from 'dayjs';
import { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { convertIfReadableDate } from './helper';
import PiiRemovalTextBox from './piiRemovalTextbox';
import RedactTextBox from './redactTextBox';
import ReportDetailsHeader from './reportDetails/reportDetailsHeader';
import ReportProgress from './reportDetails/reportProgress';
import ViewVoiceReportTranscript from './viewVoiceReportTranscript';

import { axiosInstanceForTenantAPI } from '../../../assets/utils/axiosInstance';
import { formatPiiDeleted } from '../../../assets/utils/replacePiiDeleted';
import { AdditionalInformationProps } from '../../../components/customer/report/types';
import { IS_VOICE_RECORDING } from '../../../constants/reportConstants';
import useDecryption from '../../../hooks/cryptography/decryptText';
import { useLocalizedCustomFieldData } from '../../../hooks/fetcher/useCustomFieldData';
import { CustomFieldValue } from '../../../interfaces/CustomField';
import { Language } from '../../../interfaces/Language';
import reportService from '../../../services/reportService';
import {
  setIsTranslated,
  setReport,
  setTranslateIsLoading,
  setTranslatedDetails,
} from '../../../slices/reportDataSlice';
import { AppDispatch, RootState } from '../../../utils/store';
import DecryptionLoadingAnimation from '../../common/decryptionLoadingAnimation';
import AudioPlayer from '../../reportSubmission/voiceReport/audioPlayer';

import { useTenantSlug } from '~/features/tenant/helpers';
import { useLocalizedConditionalLogicFieldData } from '~/hooks/fetcher/useCustomFieldConditionalLogicData';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { useAppSelector } from '~/hooks/global/typedReduxHooks';
import { RiskScoreChip } from '~/pages/customer/reports/report-details/RiskScoreChip';
import { ReportService } from '~/services/api/reports/endpoints';
import { useWrappedMutation } from '~/services/api/rest-api';
import { getErrorMessage } from '~/services/api/rest-api-error';

interface ReportDetailsProps {
  isLoading: boolean;
}

const bodyText2 = {
  color: 'black',
  fontFamily: '"Schibsted Grotesk", sans-serif, Arial',
  fontWeight: 600,
  fontSize: '1rem',
};

const FETCH_TRANSCRIPTION_INTERVAL = 2000;

interface TranslatedData {
  description: string;
  description_detected_language: Language[];
  reference_id: string;
  target_language: string;
}

const ReportDetails = ({ isLoading }: ReportDetailsProps) => {
  const intl = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  const { createToastNotification } = useToastNotificationContext();
  const { decryptText } = useDecryption();
  const slug = useTenantSlug();
  const [reportDescription, setReportDescription] = useState<string>('');
  const [redactedPositions, setRedactedPositions] = useState<
    [number, number][]
  >([]);
  const [transcriptionURL, setTranscriptionURL] = useState('');
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);
  const userTranslationLanguage = useSelector(
    (state: RootState) => state.languages.userTranslationLanguage,
  );

  const {
    referenceId,
    description,
    additionalInformation,
    customFields,
    archivedFields,
  } = useSelector((state: RootState) => state.reportData.report);

  const { translatedDetails, isDetailsTranslated, translateIsLoading } =
    useSelector((state: RootState) => state.reportData);
  const voiceRecording = useSelector(
    (state: RootState) => state.reportData.voiceRecording,
  );
  const descriptionDetails = useAppSelector(
    (state) => state.reportData.report.otherDescriptionDetails,
  );
  const descriptionDetectedLanguage = useSelector(
    (state: RootState) => state.reportData.descriptionDetectedLanguage,
  );
  const redactInfoState = useSelector(
    (state: RootState) => state.reportData.redactInfoState,
  );
  const redactedDescriptionPositions = useSelector(
    (state: RootState) => state.reportData.redactedDescriptionPositions,
  );
  const piiRemovalFlag = useSelector(
    (state: RootState) => state.piiRemoval.piiRemovalFlag,
  );
  const showTranslateButton =
    descriptionDetectedLanguage.length !== 0 &&
    descriptionDetectedLanguage[0] !== userTranslationLanguage.id;
  const { getLocalizedCustomFieldValues } = useLocalizedCustomFieldData();
  const { getLocalizedConditionalLogicFieldLabel } = useLocalizedConditionalLogicFieldData();
  const reportDetails = useAppSelector((state) => state.reportData.report);
  const updateRiskScores = useWrappedMutation(
    ReportService.endpoints.reportActionUpdate.useMutation(),
  );

  useEffect(() => {
    setReportDescription(description);
    if (redactedDescriptionPositions)
      setRedactedPositions(redactedDescriptionPositions);
  }, [redactedDescriptionPositions, description]);

  const recording = voiceRecording
    .at(0)
    ?.files.find((file) => file.attachmentType === IS_VOICE_RECORDING);
  const [additionalInformationState, setAdditionalInformationState] = useState<
    AdditionalInformationProps[]
  >([]);

  useEffect(() => {
    function fetchTranscription() {
      if (recording && !transcriptionURL) {
        reportService
          .getVoiceReportTranscription(recording.id)
          .then((voiceReportTranscription) => {
            if (
              voiceReportTranscription.TranscriptionJob?.Transcript
                ?.TranscriptFileUri
            ) {
              setTranscriptionURL(
                voiceReportTranscription.TranscriptionJob?.Transcript
                  ?.TranscriptFileUri,
              );
            }
          });
      }
    }

    const interval = setInterval(() => {
      fetchTranscription();
      // Clear interval if transcriptionURL has a value
      if (transcriptionURL) {
        clearInterval(interval);
      }
    }, FETCH_TRANSCRIPTION_INTERVAL);

    return () => clearInterval(interval);
  }, [recording, transcriptionURL, setTranscriptionURL]);

  // When translate button is clicked this useEffect is executed.
  // Translate the description to english.
  // This also handles the switching between original and translated content.
  useEffect(() => {
    if (isDetailsTranslated) {
      if (translatedDetails === '') {
        dispatch(setTranslateIsLoading(true));
        axiosInstanceForTenantAPI()
          .post(`report/${referenceId}/translate`, {
            target_language_id: userTranslationLanguage.id,
          })
          .then(async (response) => {
            const translated: TranslatedData = response.data;
            decryptText(translated.description)
              .then((decryptedText) => {
                dispatch(setTranslatedDetails(decryptedText));
              })
              .finally(() => {
                dispatch(setIsTranslated(true));
                dispatch(setTranslateIsLoading(false));
              });
          });
      }
    } else {
      if (translatedDetails !== '') {
        dispatch(setIsTranslated(false));
      }
    }
  }, [
    decryptText,
    referenceId,
    isDetailsTranslated,
    translatedDetails,
    userTranslationLanguage.id,
    dispatch,
  ]);

  const handleTranslate = () => {
    dispatch(setIsTranslated(!isDetailsTranslated));
  };

  useEffect(() => {
    if (!additionalInformation) return;
    Promise.all(
      additionalInformation.map(async (info: AdditionalInformationProps) => {
        if (info.descriptionText !== null) {
          return { descriptionText: info.descriptionText };
        } else {
          return { descriptionText: '' };
        }
      }),
    ).then((res) => {
      setAdditionalInformationState(res);
    });
  }, [additionalInformation]);

  useEffect(() => {
    if (piiRemovalFlag || redactInfoState) dispatch(setIsTranslated(false));
  }, [dispatch, piiRemovalFlag, redactInfoState]);

  const HighlightedRedactedDescription = () => {
    const applyHighlightAndFontStyles = () => {
      let highlightedText = '';
      let lastIndex = 0;
      const content = !isDetailsTranslated
        ? reportDescription
        : translatedDetails;

      // Sort ranges based on start index

      const newRedactedPositions = [...redactedPositions];
      if (newRedactedPositions && reportDescription) {
        newRedactedPositions.sort((a, b) => a[0] - b[0]);
        newRedactedPositions.forEach((range) => {
          const [start, end] = range;

          // Append non-highlighted text
          highlightedText += content.substring(lastIndex, start);

          // Append highlighted text
          highlightedText += `<span style="background-color: yellow">${content.substring(
            start,
            end,
          )}</span>`;

          lastIndex = end;
        });
      }
      // Append any remaining non-highlighted text
      highlightedText += content.substring(lastIndex);

      const replacedText = formatPiiDeleted(highlightedText);
      return replacedText;
    };
    return <div>{applyHighlightAndFontStyles()}</div>;
  };

  const selectTextBox = () => {
    if (piiRemovalFlag) {
      return <PiiRemovalTextBox />;
    } else if (redactInfoState) {
      return <RedactTextBox />;
    } else {
      return <HighlightedRedactedDescription />;
    }
  };

  const handleUpdateDescriptionScore = (score: number) => {
    updateRiskScores.mutateAsync(
      {
        report_reference: reportDetails.referenceId,
        payload: {
          report_informations: [
            { id: descriptionDetails.id, risk_score: score },
          ],
        },
        slug: slug,
      },
      {
        async onSuccess(data) {
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Risk score updated successfully',
            }),
            'success',
          );
          dispatch(
            setReport({
              ...reportDetails,
              otherDescriptionDetails: {
                ...reportDetails.otherDescriptionDetails,
                riskScore: score,
              },
              totalRiskScore: data.risk_score,
            }),
          );
        },
        onError(error) {
          createToastNotification(getErrorMessage(error), 'error');
        },
      },
    );
  };

  const handleUpdateCustomFieldScore = (id: number, score: number) => {
    updateRiskScores.mutateAsync(
      {
        report_reference: reportDetails.referenceId,
        payload: {
          report_custom_field_values: [{ id, risk_score: score }],
        },
        slug: slug,
      },
      {
        async onSuccess(data) {
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Risk score updated successfully',
            }),
            'success',
          );
          dispatch(
            setReport({
              ...reportDetails,
              totalRiskScore: data.risk_score,
            }),
          );
        },
        onError(error) {
          createToastNotification(getErrorMessage(error), 'error');
        },
      },
    );
  };

  return (
    <Box
      sx={{
        border: '1px solid #D6D6D6',
        borderRadius: '6px',
        flex: 1,
      }}
      bgcolor='#FFF'
      display='grid'
    >
      {/* Header */}
      <ReportDetailsHeader isLoading={isLoading} />
      <ReportProgress />
      <Divider />
      <Box
        sx={{
          px: 2,
          height: '47vh',
          overflowY: 'auto',
          scrollbarWidth: 'thin',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          justifyContent: isLoading ? 'center' : 'flex-start',
          alignItems: isLoading ? 'center' : 'flex-start',
          '::-webkit-scrollbar': {
            width: '10px',
          },
          '::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '::-webkit-scrollbar-thumb': {
            bgcolor: 'rgba(155, 155, 155, 0.5)',
            borderRadius: '20px',
            border: 'transparent',
          },
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              maxHeight: '250px',
              maxWidth: '350px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: 'auto',
            }}
          >
            <DecryptionLoadingAnimation />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                pt: 3,
                pb: 1.5,
                width: '100%',
              }}
            >
              <Box display='flex' justifyContent='space-between'>
                <Typography
                  sx={{
                    ...bodyText2,
                    fontSize: '.9rem',
                  }}
                >
                  <FormattedMessage defaultMessage='Details' />
                </Typography>
                <div className='flex gap-2 items-center'>
                  <RiskScoreChip
                    value={descriptionDetails.riskScore}
                    onBlur={handleUpdateDescriptionScore}
                    isEditable
                  />
                  {showTranslateButton &&
                    !piiRemovalFlag &&
                    !redactInfoState && (
                      <Button
                        disabled={translateIsLoading}
                        startIcon={
                          <CachedIcon
                            sx={{
                              transform: !isDetailsTranslated
                                ? 'scaleX(1)'
                                : 'scaleX(-1)',
                            }}
                          />
                        }
                        sx={{
                          textTransform: 'none',
                          fontSize: 14,
                          color: '#00CECF',
                          display: translateIsLoading ? 'none' : 'flex',
                          '&:hover': {
                            background: 'inherit',
                          },
                        }}
                        onClick={() => handleTranslate()}
                      >
                        {!isDetailsTranslated &&
                          !translateIsLoading &&
                          intl.formatMessage({ defaultMessage: 'Translate' })}
                        {isDetailsTranslated &&
                          !translateIsLoading &&
                          intl.formatMessage({ defaultMessage: 'Original' })}
                      </Button>
                    )}
                </div>
              </Box>

              {/* Inserts formatted text */}
              {reportDescription && redactedPositions ? (
                translateIsLoading ? (
                  <Skeleton variant='rectangular' height={300} />
                ) : (
                  selectTextBox()
                )
              ) : (
                <></>
              )}
            </Box>
            {additionalInformationState &&
              additionalInformationState.length > 0 && (
                <Typography
                  sx={{
                    ...bodyText2,
                    fontSize: '.9rem',
                  }}
                >
                  <FormattedMessage defaultMessage='Additional Information' />
                </Typography>
              )}
            {additionalInformationState &&
              additionalInformationState.length > 0 && (
                <Box width='100%'>
                  <Stack
                    direction='column'
                    alignItems='flex-start'
                    justifyContent='flex-start'
                    sx={{ py: 1 }}
                  >
                    {additionalInformationState.map(
                      (
                        information: AdditionalInformationProps,
                        index: number,
                      ) => (
                        <Stack key={index}>
                          <Typography
                            variant='body2'
                            sx={{
                              fontFamily: 'Schibsted Grotesk',
                              fontSize: '16px',
                              fontWeight: 400,
                              lineHeight: '24px',
                              letterSpacing: '0em',
                              textAlign: 'left',
                            }}
                          >
                            {information.descriptionText}
                          </Typography>
                        </Stack>
                      ),
                    )}
                  </Stack>
                </Box>
              )}

            {customFields &&
              customFields.length > 0 &&
              customFields.map((item: CustomFieldValue) => {
                const value = Array.isArray(item.value)
                  ? item.value.join(', ')
                  : item.value;
                const localizedValues:
                  | { title: string; value: string }
                  | undefined = getLocalizedCustomFieldValues(
                  item.title ?? '',
                  value,
                );

                const jointValue = Array.isArray(localizedValues.value)
                  ? localizedValues.value.join(', ')
                  : localizedValues.value;

                const parentRiskScore = Array.isArray(item.value)
                  ? item.value.reduce(
                      (prevScore, currentValue) =>
                        prevScore + (item.get_risk_score[currentValue] ?? 0),
                      0,
                    )
                  : item.get_risk_score[localizedValues.value];

                if (localizedValues && localizedValues.title === 'Red Flags') {
                  const redFlags = localizedValues.value;
                  const totalScore = Object.values(item.get_risk_score).reduce(
                    (prevValue, currentValue) => {
                      if (!prevValue) {
                        return 0;
                      }
                      if (!currentValue) {
                        return prevValue;
                      }
                      return prevValue + currentValue;
                    },
                    0,
                  );
                  return (
                    <div
                      key={localizedValues.title}
                      className='flex flex-col gap-2 w-full'
                    >
                      <div className='flex justify-between'>
                        <Typography fontSize={14} fontWeight={500}>
                          {localizedValues.title}
                        </Typography>
                        <RiskScoreChip
                          value={totalScore ?? 0}
                          onBlur={(score) => {
                            handleUpdateCustomFieldScore(item.id, score);
                          }}
                          isEditable
                        />
                      </div>
                      <div className='flex flex-row gap-2'>
                        {redFlags?.split(', ').map((flag) => (
                          <div
                            key={flag}
                            className='bg-brand-primary-500 rounded-2xl px-2 py-1'
                          >
                            {flag}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }

                return (
                  <Box key={localizedValues.title} width='100%'>
                    <Divider light sx={{ border: '1px solid #D6D6D6' }} />
                    <Stack
                      direction='column'
                      alignItems='flex-start'
                      justifyContent='flex-start'
                      spacing={1}
                      sx={{ pt: 2.5, pb: 2.5 }}
                    >
                      <div className='flex justify-between items-center w-full'>
                        <Typography
                          variant='body2'
                          sx={{
                            fontSize: '14px',
                            fontWeight: 500,
                            lineHeight: '18px',
                            textAlign: 'left',
                          }}
                        >
                          {localizedValues?.title}
                        </Typography>
                        <RiskScoreChip
                          value={parentRiskScore}
                          onBlur={(score) => {
                            handleUpdateCustomFieldScore(item.id, score);
                          }}
                          isEditable={
                            // This risk score chip is disabled when the field can have conditional logic
                            Object.keys(item.conditional_logic_data).length ===
                              0 && item.can_edit_risk_score
                          }
                        />
                      </div>
                      <Typography
                        variant='body2'
                        sx={{
                          fontSize: '16px',
                          fontWeight: 400,
                          lineHeight: '24px',
                          textAlign: 'left',
                        }}
                      >
                        {convertIfReadableDate(jointValue)}
                      </Typography>
                    </Stack>
                    {Object.keys(item.conditional_logic_data).length > 0 &&
                      item.conditional_logic_data.conditional_logic_answer && (
                        <div className='flex flex-col gap-3 pb-3'>
                          <div className='flex justify-between items-center w-full'>
                            <NewTypography color='secondary'>
                              {
                                getLocalizedConditionalLogicFieldLabel(item.conditional_logic_data.conditional_logic_title)
                              }
                            </NewTypography>
                            <RiskScoreChip
                              value={item.conditional_logic_data.risk_score}
                              onBlur={(score) => {
                                handleUpdateCustomFieldScore(item.id, score);
                              }}
                              isEditable
                            />
                          </div>
                          <Typography
                            variant='body2'
                            sx={{
                              fontSize: '16px',
                              fontWeight: 400,
                              lineHeight: '24px',
                              textAlign: 'left',
                            }}
                          >
                            {
                              item.conditional_logic_data
                                .conditional_logic_answer
                            }
                          </Typography>
                        </div>
                      )}
                  </Box>
                );
              })}
            {recording?.url && (
              <Divider light sx={{ border: '1px solid #D6D6D6' }} />
            )}
            {recording?.url && (
              <Box width='100%'>
                <Divider
                  flexItem
                  sx={{
                    marginTop: 2,
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: 14,
                    marginTop: '24px',
                    marginBottom: '8px',
                  }}
                >
                  <FormattedMessage defaultMessage='Recordings' />
                </Typography>
                <Stack
                  direction='row'
                  marginY={1}
                  justifyContent='space-between'
                >
                  <Stack
                    direction='row'
                    spacing={1}
                    flexGrow={1}
                    alignItems='center'
                  >
                    <Typography>
                      <FormattedMessage defaultMessage='Voice Record 1' />
                    </Typography>
                  </Stack>
                  {transcriptionURL && (
                    <Stack>
                      <Button
                        variant='text'
                        sx={{
                          textTransform: 'none',
                          color: (theme) => theme.palette.teal.main,
                        }}
                        onClick={() => {
                          setIsTranscriptOpen(true);
                        }}
                      >
                        View transcript
                      </Button>
                    </Stack>
                  )}
                </Stack>
                <AudioPlayer audioURL={recording?.url} />
                <ViewVoiceReportTranscript
                  title={intl.formatMessage({
                    defaultMessage: 'Voice Record 1',
                  })}
                  open={isTranscriptOpen}
                  onClose={() => {
                    setIsTranscriptOpen(false);
                  }}
                  audioURL={recording.url}
                  transcriptURL={transcriptionURL}
                />
              </Box>
            )}
            {archivedFields && archivedFields.length > 0 && (
              <>
                <div className='my-8 border-t border-gray-200' />
                <p className='text-xs font-semibold text-gray-500 uppercase'>
                  <FormattedMessage defaultMessage='Archived Fields' />
                </p>
              </>
            )}
            {archivedFields &&
              archivedFields.length > 0 &&
              archivedFields.map((item, index) => (
                <div key={index} className='w-full'>
                  <div className='py-4 flex flex-row items-center justify-between gap-2'>
                    <div className='flex flex-col gap-2'>
                      <h5 className='text-base font-medium text-left text-gray-700'>
                        {item.title}
                      </h5>
                      <p className='text-base text-left text-gray-500'>
                        {item.value}
                      </p>
                    </div>
                    <div className='flex flex-col gap-2'>
                      <h5 className='text-sm text-right text-gray-700'>
                        <FormattedMessage defaultMessage='Date Archived' />
                      </h5>
                      <p className='text-sm text-right font-semibold text-gray-500'>
                        {dayjs(item.deleted_at).format('MMM DD, YYYY')}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            <Box sx={{ pt: 2 }}></Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ReportDetails;
